import get from 'lodash/get';
import { allowed } from '@/utils';
import { defaultFilters } from './state';
import sumBy from 'lodash/sumBy';

export default {
	isArchived: (state, getters) => (id) => id && 'archive' === getters.getCampaign(id).status,
	isTrashed: (state, getters) => (id) => id && 'trash' === getters.getCampaign(id).status,
	isRestricted: (state, getters) => (id) => getters.isArchived(id) || getters.isTrashed(id),
	allCampaigns: (state) => get(state, 'campaigns', []),
	getCampaign: (state) => (id) => {
		let campaign = state.campaigns.filter((campaign) => campaign.id === id)[0];
		if (!campaign) {
			// This could be a split test, so let's find out.
			state.campaigns.forEach((c) => {
				if (!campaign) {
					let splits = (c.splits && c.splits.filter((split) => split.id === id)) || [];
					if (splits.length) {
						campaign = splits[0];
						campaign.sites = c.site;
						campaign.folders = c.folders;
					}
				}
			});
		}
		return campaign;
	},
	getCampaignIndex: (state) => (id) => state.campaigns.findIndex((campaign) => campaign.id === id),
	hasCampaigns: (state) => state.campaigns.length,
	/*
	 * This formula is taken from the following query on the analytics table:
	 * SELECT
	 *  IF(campaign_type = 'post', 'inline', IF(campaign_type = 'lightbox', 'popup', IF(campaign_type = 'full-page-takeover', 'fullscreen', campaign_type))) as type,
	 *  ROUND(IFNULL(((sum(conversions) * 100) / sum(impressions)), 0), 2) as rate,
	 *  format(SUM(impressions), 0) as impressions,
	 *  format(SUM(conversions), 0) as conversions
	 * FROM analytics
	 * GROUP BY type
	 * ORDER BY rate DESC
	 */
	isOnFire: (state, getters) => (id) => {
		if (getters.isRestricted(id)) {
			return false;
		}

		// Only include campaigns that have at least 100 impressions or 20 conversions.
		const campaign = getters.getCampaign(id);
		if (100 > campaign.analytics_data.impressions || 20 > campaign.analytics_data.conversions) {
			return false;
		}

		let rate = 1001;

		switch (campaign.type) {
			case 'Fullscreen':
				rate = 400;
				break;
			case 'Lightbox Popup':
			case 'Popup':
				rate = 300;
				break;
			case 'Mobile Popup':
			case 'Slide-in':
				rate = 200;
				break;
			case 'Canvas':
			case 'Inline / After Post':
			case 'Inline':
			case 'Sidebar':
			case 'Floating Bar':
				rate = 100;
				break;
		}

		return rate < campaign.analytics_data.rate * 100;
	},
	hasSplits: (state, getters) => (id) => {
		const campaign = getters.getCampaign(id) || {};
		return campaign.splits && campaign.splits.length;
	},
	canBulk: (state, getters, rootState) => {
		return state.campaigns.filter((c) => allowed(rootState.user, 'roles', 'campaign.edit', c));
	},
	campaignsWithSplits: (state) => {
		let payload = [];

		state.campaigns.forEach((campaign) => {
			payload.push(campaign);

			if (campaign.splits.length) {
				campaign.splits.forEach((split) => {
					payload.push(split);
				});
			}
		});

		return payload;
	},
	moreAvailable: (state) => state.totalFilteredCampaignsCount > state.campaigns.length,
	canResetFilters: (state) => {
		if ('all' !== state.embedFilter) {
			return true;
		}

		if (state.search) {
			return true;
		}

		const filters = defaultFilters();
		const found = Object.keys(filters).find((f) => {
			if (Array.isArray(filters[f])) {
				return get(state, `filters.${f}`).length !== filters[f].length;
			}

			return get(state, `filters.${f}`) !== filters[f];
		});

		return Boolean(found);
	},

	enabledCampaignIds: (state, getters, rootState) => {
		return rootState.wp.campaigns.filter((c) => '1' === get(c, 'post_meta._omapi_enabled', '')).map((c) => c.id);
	},
	rawDisabledCampaignIds: (state, getters, rootState) => {
		return rootState.wp.campaigns.filter((c) => '1' !== get(c, 'post_meta._omapi_enabled', '')).map((c) => c.id);
	},
	allWpIds: (state, { rawDisabledCampaignIds, enabledCampaignIds }) => {
		return enabledCampaignIds.concat(rawDisabledCampaignIds);
	},
	missingIds: (state, { allCampaigns, allWpIds }) => {
		return allCampaigns.filter((c) => !allWpIds.includes(c.id)).map((c) => c.id);
	},
	disabledCampaignIds: (state, { rawDisabledCampaignIds, missingIds }) => {
		return rawDisabledCampaignIds.concat(missingIds);
	},
	publishCount: (state) => state.publishCount,
	pendingCount: ({ totalCampaignsCount, publishCount, draftCount }, { missingIds }) => {
		return Math.max(0, totalCampaignsCount - publishCount - draftCount - missingIds.length);
	},
	draftCount: ({ draftCount }, { missingIds }) => draftCount + missingIds.length,
	hasError: (state, getters, rootState, rootGetters) => {
		return !!state.error || rootGetters.hasSiteError || rootGetters['wp/hasError'];
	},
	allConversions: (state, getters) => sumBy(getters.allCampaigns, 'analytics_data.conversions'),
};
